import { useState, useEffect } from "react";
// ...existing code...
import client from "@/Graphql/apolloClient";
import { CONTACT_US_MUTATION, COUNTRY_CODE_LIST } from "@/Graphql/Queries";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
// ...existing code...
import { z } from "zod";
// import { insertContactSchema } from "@shared/schema";

import { Phone, Mail } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { normalizeDefault, isValidEmail, isValidPhoneNumber } from "@/utils/validationUtils";
import type { BasicField, Configuration, Field, FooterConfig } from "@/types/config";
import logger from '@/utils/logger';

type ContactFormData = {
  fullName: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  companyName: string;
  // address: string;
  // budget: string;
  message: string;
  error: string | null;
  fieldErrors: Record<string, string>;
};

interface ContactFormProps {
  countryName: string;
  contentData: any[];
  configData: any[];
}

export default function ContactForm({ countryName, contentData, configData }: ContactFormProps) {
  // Validation helper functions
  const validateEmail = (email: string): string => {
    if (!email.trim()) {
      return "Please enter your valid email ID";
    } else if (!isValidEmail(email)) {
      return "Invalid email format";
    }
    return "";
  };

  const validatePhoneNumber = (phoneNumber: string, countryCode: string): string => {
    if (!phoneNumber.trim()) {
      return "Please enter your phone number";
    } else if (!isValidPhoneNumber(countryCode + phoneNumber)) {
      return "Invalid phone number format";
    }
    return "";
  };

  // Dynamic currency symbol and ISO based on country code
  const [selectedCurrency, setSelectedCurrency] = useState<{ currency_symbol: string; iso: string }>({ currency_symbol: '', iso: '' });

  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState<ContactFormData>({
    fullName: "",
    email: "",
    phoneNumber: "",
    countryCode: "",
    companyName: "",
    // address: "",
    // budget: "",
    message: "",
    error: null,
    fieldErrors: {},
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [countryCodes, setCountryCodes] = useState<{ code: string; country: string; currencySymbol?: string; ISO?: string }[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCountries, setFilteredCountries] = useState<{ code: string; country: string; currencySymbol?: string; ISO?: string }[]>([]);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [contactusSubHeadline, setcontactusSubHeadline] = useState<string>("");
  const [contactusHeadline, setcontactusHeadline] = useState<string>("");
  const [contactImage, setContactImage] = useState('');
  const [showFooter, setShowFooter] = useState<FooterConfig | null>(null);
  const [email, setEmail] = useState<string>("");
  const [mobileNumber, setMobileNumber] = useState<string>("");
  const [mailConfig, setMailConfig] = useState({
    host: "",
    port: "",
    username: "",
    password: ""
  });


  // Fetch country code list from API
  useEffect(() => {
    const fetchCountryCodeList = async () => {
      try {
        const { data } = await client.query({
          query: COUNTRY_CODE_LIST,
        });
        if (data?.countryCodeList) {
          setCountryCodes(data.countryCodeList);
          setFilteredCountries(data.countryCodeList);
        } else {
          setCountryCodes([]);
          setFilteredCountries([]);
        }
      } catch (error) {
        setCountryCodes([]);
        setFilteredCountries([]);
      }
    };
    fetchCountryCodeList();
  }, []);

  useEffect(() => {
    if (countryCodes.length > 0) {
      let defaultCountry = null;
      // First, try to find a match for +65
      defaultCountry = countryCodes.find(c => c.code === "+65");
      // If no match for +65, try to find a match based on countryName prop
      if (!defaultCountry && countryName) {
        defaultCountry = countryCodes.find(c => c.country.toLowerCase() === countryName.toLowerCase());
      }
      // If still no match found, use the first country as default
      if (!defaultCountry) {
        defaultCountry = countryCodes[0];
      }
      if (defaultCountry) {
    
        setFormData((prev: any) => ({ ...prev, countryCode: defaultCountry.code }));
        setSelectedCurrency({ 
          currency_symbol: defaultCountry.currencySymbol || '', 
          iso: defaultCountry.ISO || '' 
        });
      }
    }
  }, [countryCodes, countryName]);

  useEffect(() => {
    if (searchQuery === "") {
      setFilteredCountries(countryCodes);
    } else {
      setFilteredCountries(
        countryCodes.filter((item) =>
          item.country.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.code.includes(searchQuery)
        )
      );
    }
  }, [searchQuery, countryCodes]);

  // Get currency symbol for selected country code, fallback to ISO if empty
  const currencySymbol = selectedCurrency.currency_symbol || selectedCurrency.iso || "";

  // Set currencySymbol only when selectedCurrency has data
  useEffect(() => {
    
  }, [currencySymbol]);


  // Sync selectedCurrency with formData.countryCode
  useEffect(() => {
    if (formData.countryCode) {
      const match = countryCodes.find(c => c.code === formData.countryCode);
      if (match) {
        setSelectedCurrency({ currency_symbol: match.currencySymbol || '', iso: match.ISO || '' });
      }
    }
  }, [formData.countryCode, countryCodes]);

  const fetchNotificationEmail = async () => {
    let toEmailAddress = "";
    try {
      const configResponse = await fetch(`${import.meta.env.VITE_APP_FOLDER_PATH}/json/configJson.json?t=${new Date().getTime()}`);
      if (configResponse.ok) {
        const configData = await configResponse.json();
        const contactUsSection = configData.find((section: any) => section.name.toLowerCase().includes("contact us"));
        if (contactUsSection && contactUsSection.configurations) {
          const emailField = contactUsSection.configurations.find((field: any) => field.key === "notification_email");
          if (emailField && emailField.default) {
            const emailValue = emailField.default;
            toEmailAddress = emailValue;
          }
        }
      }
      if (!toEmailAddress) {
        const contentResponse = await fetch(`${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`);
        if (contentResponse.ok) {
          const contentData = await contentResponse.json();
          const contactUsSection = contentData.find((section: any) => section.name.toLowerCase().includes("contact us"));
          if (contactUsSection) {
            const emailField = contactUsSection.fields.find((field: any) => field.key === "email");
            if (emailField && emailField.defaultValue) {
              toEmailAddress = emailField.defaultValue;
            }
          }
        }
      }
    } catch (err) {
      // fallback
    }
    return toEmailAddress || "";
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // Validate email
    const emailError = validateEmail(formData.email);
    if (emailError) {
      setError(emailError);
      setFormData(prev => ({ ...prev, fieldErrors: { ...prev.fieldErrors, email: emailError } }));
      setLoading(false);
      toast({
        title: "Validation Error",
        description: emailError,
        variant: "destructive",
      });
      return;
    }

    // Validate phone number
    const phoneError = validatePhoneNumber(formData.phoneNumber, formData.countryCode);
    if (phoneError) {
      setError(phoneError);
      setFormData(prev => ({ ...prev, fieldErrors: { ...prev.fieldErrors, phoneNumber: phoneError } }));
      setLoading(false);
      toast({
        title: "Validation Error",
        description: phoneError,
        variant: "destructive",
      });
      return;
    }

    const toEmailAddress = await fetchNotificationEmail();
    // const body = `The following individual reached out to you via the OSS contact us page.\nFull Name: ${formData.fullName}\nEmail: ${formData.email}\nPhone: ${formData.countryCode}${formData.phoneNumber}\nCompany: ${formData.companyName}\nAddress: ${formData.address}\nBudget: ${formData.budget}\nMessage: ${formData.message}`;
    const body = JSON.stringify({
      moName: "Moving Walls Sdn Bhd",
      recipientName: formData.fullName,
      fullName: formData.fullName,
      email: formData.email,
      phoneNumber: `${formData.countryCode}${formData.phoneNumber}`,
      company: formData.companyName,
      // address: formData.address,
      // campaignBudget: formData.budget,
      message: formData.message,
      contactEmail: "market@movingwalls.com",
      // contactPhone: "+60123156123",
      currentYear: new Date().getFullYear(),
    });


    try {
      const response = await client.mutate({
        mutation: CONTACT_US_MUTATION,
        variables: {
          body,
          toAddress: [toEmailAddress],
          template: "online_contactUsEmailAdmin.tpl",
          mailConfig: {
            host: mailConfig.host,
            port: Number(mailConfig.port),
            username: mailConfig.username,
            password: mailConfig.password,
          },
        },
      });
      if (response.data.contactUs.status === "success") {
        setSuccess(true);
        setFormData({
          fullName: "",
          email: "",
          phoneNumber: "",
          countryCode: "+65",
          companyName: "",
          // address: "",
          // budget: "",
          message: "",
          error: null,
          fieldErrors: {},
        });
        toast({
          title: "Message Sent!",
          description: "Thank you for your interest. We'll get back to you within 24 hours.",
        });
      } else {
        setError("Failed to send message.");
        toast({
          title: "Error",
          description: "Failed to send message. Please try again.",
          variant: "destructive",
        });
      }
    } catch (err) {
      setError("Failed to send message. Please try again.");
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoadingConfig(true);
        // Example: set landing page flag and logo
        const landingPageConfig = configData.find((page: any) => page.name.toLowerCase().includes("landing page"));

        if (landingPageConfig) {
          const fields: Field[] = landingPageConfig.configurations.map((cfg: Configuration) => {
            if (cfg.fields) {
              return {
                key: cfg.key,
                label: cfg.label,
                fields: cfg.fields.map((field) => ({
                  key: field.key,
                  type: field.type,
                  label: field.label,
                  default: normalizeDefault(field.default),
                  disabled: field.disabled,
                })),
              };
            } else {
              return {
                key: cfg.key,
                type: cfg.type || "unknown",
                label: cfg.label,
                default: normalizeDefault(cfg.default),
                disabled: cfg.disabled,
              };
            }
          });

          const footerConfig = fields.find((item) => item.key === "footer") as FooterConfig;
          setShowFooter(footerConfig);
        }

        const TechConfigSection = configData.find((section: any) => section.name.toLowerCase().includes("tech configuration"));
        if (TechConfigSection && TechConfigSection.configurations) {
          const smtpConfig = TechConfigSection.configurations.find((field: any) => field.key === "smtp configuration");
          if (smtpConfig && smtpConfig.fields) {
            const getValue = (key: string) =>
              smtpConfig.fields.find((f: any) => f.key === key)?.default || "";

            setMailConfig({
              host: getValue("host name1"),
              port: getValue("port number1"),
              username: getValue("username1"),
              password: getValue("password1"),
            });
          }

        }

      } catch (error) {
        setLoadingConfig(true);
      } finally {
        setLoadingConfig(false);
      }
    };
    fetchConfig();
  }, [configData]);

  const isFieldEnabled = (key: string, config: any[]) => {
    const landingPageConfig = config?.find((page: { name: string }) => page.name.toLowerCase().includes("contact us"));

    const configdata = landingPageConfig?.configurations || [];
    const fieldConfig = configdata?.find((field: any) => field.key === key);
    return fieldConfig ? normalizeDefault(fieldConfig.default) : true;
  };

  const isFieldEnabledfooterfunction = (key: string, config: FooterConfig | null) => {
    if (!config || !config.fields) {
      return true;
    }
    const fieldConfig = config.fields.find((field) => field.key === key);
    return fieldConfig ? normalizeDefault((fieldConfig as BasicField).default) : true;
  };

  const isFieldEnabledFooter = (key: string) => isFieldEnabledfooterfunction(key, showFooter);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_APP_FOLDER_PATH}/json/contentJson.json?t=${new Date().getTime()}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch contentJson.json: ${response.statusText}`);
        }
        const data = await response.json();
        const value = data.find((page: any) => page.name === "Landing Page");
        

        if (value?.fields) {
          // Find the Footers panel
          let footersPanel;
          for (const field of value.fields) {
            if (Array.isArray(field.components)) {
              footersPanel = field.components.find((comp: any) => comp.type === "panel" && comp.title?.includes("Footer"));
              if (footersPanel) break;
            }
          }
          if (footersPanel && Array.isArray(footersPanel.components)) {
            const emailField = footersPanel.components.find((field: any) => field.key === "email");
            const mobileField = footersPanel.components.find((field: any) => field.key === "MobileNumber");
            setEmail(emailField?.defaultValue || "");
            setMobileNumber(mobileField?.defaultValue || "");
          } else {
            setEmail("");
            setMobileNumber("");
          }
        }

        if (!value) {
          logger.error("Landing Page not found in contentJson.json");
          return;
        }

        const contactImageField = value.fields.find((field: any) => field.key === "Contactusform");
        if (!contactImageField) {
          logger.error("Contactusform field not found in Landing Page");
          return;
        }

        const components = contactImageField.components?.[0]?.components;
        if (!components || components.length === 0) {
          logger.error("No components found in Contactusform");
          return;
        }

        for (let i = 0; i < components.length; i++) {
          const component = components[i];
          if ('key' in component) {
            const key = component.key;
            const value = component.defaultValue;

            if (key === "Contactheadline") {
              setcontactusHeadline(value || "Any questions?");
            } else if (key === "contactsubHeadline") {
              setcontactusSubHeadline(value || "Write to us. We will get back to you within 24 hrs.");
            } else if (key === "contanctusClientImage" && value && value.length > 0) {
              let imgUrl = '';
              if (value[0].url) {
                imgUrl = `${import.meta.env.VITE_APP_FOLDER_PATH}/${value[0].url}`;
              } else if (value[0].data?.url) {
                imgUrl = `${import.meta.env.VITE_APP_FOLDER_PATH}/${value[0].data.url}`;
              }
              if (imgUrl) {
                setContactImage(imgUrl);
              } else {
                logger.error("No valid URL found for contanctusClientImage");
              }
            }
          }
        }
      } catch (error) {
        logger.error("Error fetching content:", error);
      }
    };

    fetchContent();
  }, []);

  if (success) {
    return (
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Thank You!</h2>
            <p className="text-xl text-gray-600 mb-8">
              Your message has been sent successfully. We'll get back to you within 24 hours.
            </p>
            <Button
              onClick={() => {
                setIsSubmitted(false);
                setSuccess(false);
              }}
              className="bg-primary-600 hover:bg-primary-700 text-white"
              data-testid="button-send-another-message"
            >
              Send Another Message
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight FontStyles mb-3 sm:mb-4">
              <span className="text-gray-900 block">{contactusHeadline || "Contact Us"}</span>
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              {contactusSubHeadline || "Get in touch with our team to discuss your OOH advertising needs. We're here to help you create impactful campaigns that deliver results."}
            </p>

            <div className="space-y-6">
              {/* {isFieldEnabledFooter("mobile") && (
                <div className="flex items-center">
                  <div className="bg-primary-600 text-white rounded-full p-4 mr-4">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Phone</div>
                    <div className="text-gray-600">{mobileNumber}</div>
                  </div>
                </div>
              )} */}
              {isFieldEnabledFooter("contact email") && (
                <div className="flex items-center">
                  <div className="bg-forest-custom text-white rounded-full p-4 mr-4">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Email</div>
                    <div className="text-gray-600">{email}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-warm-white rounded-3xl p-8 shadow-xl">
            <form onSubmit={onSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isFieldEnabled("fullName", configData) && (
                  <div>
                    <label className="font-semibold block mb-1">Full Name</label>
                    <Input
                      placeholder="Full Name"
                      name="fullName"
                      value={formData.fullName}
                      onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                      className="h-12 border-0 bg-white shadow-sm rounded-2xl"
                    />
                  </div>
                )}
                {isFieldEnabled("e-mail", configData) && (
                  <div>
                    <label className="font-semibold block mb-1">Email</label>
                    <Input
                      placeholder="Email"
                      name="email"
                      value={formData.email}
                      onChange={e => {
                        setFormData({ ...formData, email: e.target.value });
                        // Real-time validation
                        const emailError = validateEmail(e.target.value);
                        setFormData(prev => ({ ...prev, fieldErrors: { ...prev.fieldErrors, email: emailError } }));
                      }}
                      className={`h-12 border-0 bg-white shadow-sm rounded-2xl ${formData.fieldErrors.email ? 'ring-2 ring-red-500' : ''}`}
                    />
                    {formData.fieldErrors.email && (
                      <p className="text-red-500 text-sm mt-1">{formData.fieldErrors.email}</p>
                    )}
                  </div>
                )}
              </div>
              {isFieldEnabled('contactNumber', configData) && (
                <div className="w-full">
                  <label className="font-semibold block mb-1">Phone Number</label>
                  <div className="flex gap-2">
                    <div className="w-1/4">
                      <DropdownMenu modal={false}>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="h-12 px-3 bg-white border border-gray-200 rounded-2xl text-sm font-medium text-gray-700 hover:bg-gray-100 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors w-full"
                          >
                            {formData.countryCode || "Select Country Code"}
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-64 bg-white border border-gray-200 shadow-md" side="top" align="start" sideOffset={5}>
                          <div className="px-3 py-2">
                            <Input
                              type="text"
                              placeholder="Search Country..."
                              value={searchQuery}
                              onChange={e => setSearchQuery(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault()    
                                }
                                e.stopPropagation()     
                              }}
                              className="pl-2 h-9 text-sm w-full"
                            />
                          </div>
                          <div className="max-h-60 overflow-y-auto">
                            {filteredCountries.map((country, index) => (
                              <DropdownMenuItem
                                key={`${country.code}-${country.country}-${index}`}
                                onClick={() => {
                                  setFormData({ ...formData, countryCode: country.code });
                                  setSelectedCurrency({ currency_symbol: country.currencySymbol || '', iso: country.ISO || '' });

                                  setSearchQuery("");
                                  
                                  // Re-validate phone number with new country code if phone exists
                                  if (formData.phoneNumber.trim()) {
                                    const phoneError = validatePhoneNumber(formData.phoneNumber, country.code);
                                    setFormData(prev => ({ ...prev, fieldErrors: { ...prev.fieldErrors, phoneNumber: phoneError } }));
                                  }
                                }}
                                className="flex justify-between text-sm cursor-pointer"
                              >
                                <span>{country.country}</span>
                                <span>{country.code}</span>
                              </DropdownMenuItem>
                            ))}
                            {filteredCountries.length === 0 && (
                              <DropdownMenuItem disabled className="text-sm text-gray-500">
                                No countries found
                              </DropdownMenuItem>
                            )}
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="w-full">
                      <Input
                        type="tel"
                        placeholder="1234567890"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={e => {
                          setFormData({ ...formData, phoneNumber: e.target.value });
                          // Real-time validation
                          const phoneError = validatePhoneNumber(e.target.value, formData.countryCode);
                          setFormData(prev => ({ ...prev, fieldErrors: { ...prev.fieldErrors, phoneNumber: phoneError } }));
                        }}
                        className={`h-12 border-0 bg-white shadow-sm rounded-2xl ${formData.fieldErrors.phoneNumber ? 'ring-2 ring-red-500' : ''}`}
                      />
                    </div>
                  </div>
                  {formData.fieldErrors.phoneNumber && (
                    <p className="text-red-500 text-sm mt-1">{formData.fieldErrors.phoneNumber}</p>
                  )}
                </div>
              )}
              {isFieldEnabled('companyName', configData) && (
                <div>
                  <label className="font-semibold block mb-1">Company</label>
                  <Input
                    placeholder="Company"
                    name="companyName"
                    value={formData.companyName}
                    onChange={e => setFormData({ ...formData, companyName: e.target.value })}
                    className="h-12 border-0 bg-white shadow-sm rounded-2xl"
                  />
                </div>
              )}
              {/* {isFieldEnabled("address", configData) && (
                <div>
                  <label className="font-semibold block mb-1">Address</label>
                  <Input
                    placeholder="Your Address"
                    name="address"
                    value={formData.address}
                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                    className="h-12 border-0 bg-white shadow-sm rounded-2xl"
                  />
                </div>
              )} */}
              {/* <div>
                <label className="font-semibold block mb-1">Campaign Budget</label>
                <Select
                  value={formData.budget}
                  onValueChange={value => setFormData({ ...formData, budget: value })}
                >
                  <SelectTrigger className="h-12 border-0 bg-white shadow-sm rounded-2xl" data-testid="select-budget">
                    <SelectValue
                      placeholder={currencySymbol ? `Select budget range (${currencySymbol})` : "Select budget range"}
                      key={currencySymbol}
                    />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-2xl">
                    <SelectItem value={`${currencySymbol}1,000 - ${currencySymbol}5,000`}>{currencySymbol}1,000 - {currencySymbol}5,000</SelectItem>
                    <SelectItem value={`${currencySymbol}5,000 - ${currencySymbol}10,000`}>{currencySymbol}5,000 - {currencySymbol}10,000</SelectItem>
                    <SelectItem value={`${currencySymbol}10,000 - ${currencySymbol}25,000`}>{currencySymbol}10,000 - {currencySymbol}25,000</SelectItem>
                    <SelectItem value={`${currencySymbol}25,000+`}>{currencySymbol}25,000+</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}
              {isFieldEnabled("message", configData) && (
                <div>
                  <label className="font-semibold block mb-1">Message</label>
                  <Textarea
                    placeholder="Tell us about your campaign goals..."
                    name="message"
                    value={formData.message}
                    onChange={e => setFormData({ ...formData, message: e.target.value })}
                    className="border-0 bg-white shadow-sm resize-none rounded-2xl"
                    rows={4}
                  />
                </div>
              )}
              <button
                type="submit"
                disabled={loading || !formData.fullName || !formData.email || !formData.phoneNumber || !formData.countryCode || !formData.companyName || !formData.message || !!formData.fieldErrors.email || !!formData.fieldErrors.phoneNumber}
                className="w-full h-12 btn-primary-gradient hover:btn-primary-gradient text-white font-semibold inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-emerald-500/40 rounded-2xl"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
              {error && (
                <p className="text-red-500 text-sm mt-2">{error}</p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
